import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button, Tab, TabList, Tabs, Tag } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
  PAGE_SIZES,
} from "../../components/carbon/DataTable/DataTable";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import { usePaginatedUsersByCompany } from "../../hooks/useUsers";
import useDebounce from "../../hooks/useDebounce";
import { useUser } from "../../context";
import { UserRole, userCompanyTypeMap } from "../../types";
import { ABILITIES } from "../../utils";
import UserGroups from "./UserGroups/UserGroups";
import "./users.scss";

interface UserRow extends DataTableRow {
  id: string;
  name: ReactNode;
  email: string;
  phone: string;
  responsible: string;
  role: ReactNode;
  status: ReactNode;
}

const Users = () => {
  const { can } = useUser();
  const { t } = useTranslation();

  const [selectedTab, setSelectedTab] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const { debouncedSearchTerm } = useDebounce(searchTerm, 400);

  const canManageGroups = can(ABILITIES.MANAGE_GROUPS);

  // Ordered identities of the tabs actually shown. Groups is optional, so the selected index is
  // resolved against this list (never a hard-coded 1=Groups). Roles moved to its own /roles page.
  const tabs: ("users" | "groups")[] = [
    "users",
    ...(canManageGroups ? (["groups"] as const) : []),
  ];
  const activeTab = tabs[selectedTab] ?? "users";

  // A new search always restarts at the first page.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm]);

  const { data: usersPage, isLoading } = usePaginatedUsersByCompany(
    page,
    pageSize,
    debouncedSearchTerm
  );

  // The backend does not exclude the caller from this list — seeing your own
  // row in your own company's roster is expected here, and it keeps the
  // server's page counts (and Pagination's arithmetic) honest.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibleUsers = usersPage?.content ?? [];

  // Genuinely nothing to manage yet, as opposed to a search that merely
  // narrowed the roster to nothing — DataTable's `empty` needs to tell
  // those two apart, see the `empty` node built below.
  const isTrulyEmpty =
    !isLoading && visibleUsers.length === 0 && !debouncedSearchTerm;

  const canManageUsers =
    can(ABILITIES.MANAGE_USERS) || can(ABILITIES.SELLER_MANAGE_USERS);

  const addAction = canManageUsers ? (
    <Button as={Link} to="/users/new" renderIcon={Add}>
      {t("users.add")}
    </Button>
  ) : null;

  const rows: UserRow[] = useMemo(
    () =>
      visibleUsers.map((user) => ({
        id: user.id,
        name: <Link to={`/users/${user.id}`}>{user.fullName}</Link>,
        email: user.email,
        phone: user.phone,
        responsible: user.parent?.fullName || "-",
        role: (
          <div className="users__role-cell">
            {user.role === UserRole.BLOCKED
              ? t("roles.blocked")
              : userCompanyTypeMap[
                  user.userCompanyType as keyof typeof userCompanyTypeMap
                ]}
            {user.role === UserRole.ADMIN_APPROVAL ? (
              <Tag type="cyan" size="sm">
                {t("userTypes.pendingApproval")}
              </Tag>
            ) : null}
            {(user.roles ?? []).map((r) => (
              <Tag key={r.id} type="gray" size="sm">
                {r.name}
              </Tag>
            ))}
          </div>
        ),
        status: user.active ? (
          <Tag type="green" size="sm">
            {t("cta.active")}
          </Tag>
        ) : (
          <Tag type="gray" size="sm">
            {t("cta.inactive")}
          </Tag>
        ),
      })),
    [visibleUsers, t]
  );

  const headers: DataTableColumn<UserRow>[] = useMemo(
    () => [
      { key: "name", header: t("users.name") },
      { key: "email", header: t("users.email") },
      { key: "phone", header: t("users.phone") },
      { key: "responsible", header: t("users.responsible") },
      { key: "role", header: t("users.role") },
      { key: "status", header: t("users.status") },
    ],
    [t]
  );

  const empty = debouncedSearchTerm ? (
    <EmptyState
      title={t("users.noResults")}
      action={
        <Button kind="ghost" onClick={() => setSearchTerm("")}>
          {t("table.clearSearch")}
        </Button>
      }
    />
  ) : (
    <EmptyState title={t("users.noUsers")} action={addAction ?? undefined} />
  );

  return (
    <div>
      <PageHeader
        title={t("users.title")}
        action={!isTrulyEmpty ? addAction : null}
      />

      {canManageGroups ? (
        <div className="users__tabs">
          <Tabs
            selectedIndex={selectedTab}
            onChange={({ selectedIndex }) => setSelectedTab(selectedIndex)}
          >
            <TabList aria-label={t("users.title")}>
              {tabs.map((tab) => (
                <Tab key={tab}>
                  {t(tab === "users" ? "users.tabUsers" : "users.tabGroups")}
                </Tab>
              ))}
            </TabList>
          </Tabs>
        </div>
      ) : null}

      {activeTab === "groups" && canManageGroups ? (
        <UserGroups />
      ) : (
        <DataTable
          headers={headers}
          rows={rows}
          isLoading={isLoading}
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            label: t("users.search"),
          }}
          pagination={{
            // DataTable's Pagination (Carbon) is 1-indexed; the roster's own
            // paging state — and the paginated-users query it drives — stays
            // 0-indexed to match the API, so the boundary converts both ways.
            page: page + 1,
            pageSize,
            pageSizes: PAGE_SIZES,
            totalItems: usersPage?.totalElements ?? 0,
            onPageChange: (newPage, newPageSize) => {
              setPage(newPage - 1);
              setPageSize(newPageSize);
            },
          }}
          empty={empty}
        />
      )}
    </div>
  );
};

export default Users;
